import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import AbstractListView from '@UI/Store/AbstractListView';
import type WorkflowRunSummaryView from '@UI/Store/WorkflowRuns/Views/WorkflowRunSummaryView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `facilityId`, `typeKey` and `subjectId` are the scope this list belongs to: `stopOn` compares them
 * against the dossier on screen (`isFor` for the dossier). Rows stay ordered by `openedAt` descending,
 * the server's order; a row sits in `inProgress` or in `completed` according to its own `completedAt`.
 */
class BeneficiaryWorkflowRunsListView extends AbstractListView<WorkflowRunSummaryView> {
	readonly facilityId: number;
	readonly typeKey: string;
	readonly subjectId: number;
	readonly totalRecords: number;

	constructor(
		list: readonly WorkflowRunSummaryView[],
		facilityId: number,
		typeKey: string,
		subjectId: number,
		totalRecords: number,
	) {
		super(list, 'BeneficiaryWorkflowRunsListView');

		this.facilityId = facilityId;
		this.typeKey = typeKey;
		this.subjectId = subjectId;
		this.totalRecords = totalRecords;

		Object.freeze(this);
	}

	isFor(subject: SubjectAddress): boolean {
		return this.typeKey === subject.typeKey && this.subjectId === subject.subjectId;
	}

	/** Runs not yet completed, in the server's order. */
	get inProgress(): readonly WorkflowRunSummaryView[] {
		return this.list.filter((run) => run.completedAt === null);
	}

	/** Runs whose `completedAt` is set, in the server's order. */
	get completed(): readonly WorkflowRunSummaryView[] {
		return this.list.filter((run) => run.completedAt !== null);
	}

	/** Replaces the row in place; a row the list does not hold is never added. */
	updateWorkflowRun(workflowRun: WorkflowRunSummaryView): BeneficiaryWorkflowRunsListView {
		if (this.findById(workflowRun.id) === null) {
			return this;
		}

		return new BeneficiaryWorkflowRunsListView(
			this.list.map((entry) => (entry.id === workflowRun.id ? workflowRun : entry)),
			this.facilityId,
			this.typeKey,
			this.subjectId,
			this.totalRecords,
		);
	}

	/** Same instance when no row carries that `id`, so a replayed removal changes nothing. */
	removeWorkflowRun(runId: number): BeneficiaryWorkflowRunsListView {
		if (this.findById(runId) === null) {
			return this;
		}

		return new BeneficiaryWorkflowRunsListView(
			this.list.filter((entry) => entry.id !== runId),
			this.facilityId,
			this.typeKey,
			this.subjectId,
			this.totalRecords - 1,
		);
	}
}

export default BeneficiaryWorkflowRunsListView;
